<template>
  <v-window-item value="materjalid">
    <v-container :class="{ 'school-courses-view': isSchoolUser }">
      <!-- Create New Course Section -->
      <v-card class="mb-6" v-if="!showCreateForm">
        <v-card-title>
          <v-btn
            @click="showCreateForm = true"
            variant="elevated"
            class="text-none"
          >
            Create New Course
          </v-btn>
        </v-card-title>
      </v-card>

      <!-- Create Course Form -->
      <v-card class="mb-6" v-if="showCreateForm">
        <v-card-title>Create New Course</v-card-title>
        <v-card-text>
          <v-form ref="createFormRef" @submit.prevent="createCourse">
            <v-text-field
              v-model="newCourse.name"
              label="Course Name"
              :rules="[rules.required, rules.maxLength(100)]"
              required
              class="mb-3"
            ></v-text-field>

            <v-textarea
              v-model="newCourse.description"
              label="Description (Optional)"
              :rules="[rules.maxLength(500)]"
              rows="3"
              class="mb-3"
            ></v-textarea>

            <v-select
              v-model="newCourse.type"
              :items="courseTypeOptions"
              label="Course Type"
              :rules="[rules.required]"
              required
              class="mb-3"
            ></v-select>

            <v-select
              v-model="newCourse.courseLanguage"
              :items="courseLanguageOptions"
              label="Course Language"
              :rules="[rules.required]"
              required
              class="mb-3"
            ></v-select>

            <v-checkbox
              v-model="newCourse.availableInFree"
              label="Available in Free Tier"
              class="mb-3"
            ></v-checkbox>

            <v-card-actions>
              <v-btn type="submit" variant="elevated" :loading="isCreating">
                Create Course
              </v-btn>
              <v-btn @click="cancelCreate" variant="outlined">Cancel</v-btn>
            </v-card-actions>
          </v-form>
        </v-card-text>
      </v-card>

      <!-- Upload Materials Section -->
      <v-card class="mb-6">
        <v-card-title>Upload Materials to Course</v-card-title>
        <v-card-text>
          <v-select
            v-model="selectedCourseForUpload"
            :items="coursesForUpload"
            item-title="name"
            item-value="id"
            label="Select Course"
            class="mb-3"
            :disabled="courses.length === 0"
          ></v-select>

          <v-file-input
            v-model="uploadFile"
            label="Select Excel File (.xlsx)"
            accept=".xlsx"
            :rules="[rules.fileFormat, rules.fileSize]"
            :disabled="!selectedCourseForUpload"
            class="mb-3"
          ></v-file-input>

          <v-btn
            @click="uploadMaterials"
            variant="elevated"
            :disabled="!selectedCourseForUpload || !uploadFile"
            :loading="isUploading"
            class="text-none"
          >
            Upload Materials
          </v-btn>
          <div v-if="uploadError" class="mt-3 text-error">{{ uploadError }}</div>
          <div v-if="uploadSuccess" class="mt-3 text-success">{{ uploadSuccess }}</div>
        </v-card-text>
      </v-card>

      <!-- Courses List -->
      <v-card>
        <v-card-title>All Courses</v-card-title>
        <v-card-text>
          <div v-if="isLoadingCourses">Loading courses...</div>
          <div v-else-if="courses.length === 0">No courses found</div>
          <div v-else>
            <v-expansion-panels v-model="expandedCourses" multiple>
              <v-expansion-panel
                v-for="course in courses"
                :key="course.id"
                :value="course.id"
              >
                <v-expansion-panel-title>
                  <div class="d-flex justify-space-between align-center" style="width: 100%">
                    <div>
                      <strong>{{ course.name }}</strong>
                      <span class="ml-3 text-caption">
                        ({{ course.type }} - {{ course.courseLanguage }})
                      </span>
                    </div>
                    <v-btn
                      @click.stop="openEditDialog(course)"
                      icon="mdi-pencil"
                      size="small"
                      variant="text"
                      class="mr-2"
                    ></v-btn>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <div class="mb-3">
                    <strong>Description:</strong> {{ course.description || "No description" }}
                  </div>
                  <div class="mb-3">
                    <strong>Available in Free:</strong> {{ course.availableInFree ? "Yes" : "No" }}
                  </div>
                  <div class="mb-3" v-if="course.wordCount !== undefined">
                    <strong>Total Words:</strong> {{ course.wordCount }}
                  </div>
                  <div class="mb-3" v-if="course.topicCount !== undefined">
                    <strong>Total Topics:</strong> {{ course.topicCount }}
                  </div>

                  <!-- Topics Section -->
                  <div v-if="courseTopics[course.id]">
                    <v-divider class="my-3"></v-divider>
                    <strong class="mb-2 d-block">Topics:</strong>
                    <v-expansion-panels v-model="expandedTopics[course.id]" multiple>
                      <v-expansion-panel
                        v-for="topic in courseTopics[course.id]"
                        :key="topic.id"
                        :value="topic.id"
                      >
                        <v-expansion-panel-title>
                          <div class="d-flex justify-space-between align-center" style="width: 100%">
                            <div>
                              {{ topic.name }}
                              <span class="ml-3 text-caption" v-if="topic.wordCount">
                                ({{ topic.wordCount }} words)
                              </span>
                            </div>
                          </div>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <div v-if="topic.description" class="mb-2">
                            <strong>Description:</strong> {{ topic.description }}
                          </div>
                          <div class="mb-2">
                            <strong>Available in Free:</strong> {{ topic.availableInFree ? "Yes" : "No" }}
                          </div>
                          <div v-if="topic.wordCount !== undefined">
                            <strong>Words in Topic:</strong> {{ topic.wordCount }}
                          </div>
                          <div v-if="topic.completedWords !== undefined && topic.completedSentences !== undefined">
                            <strong>Progress:</strong> {{ topic.completedWords }}/{{ topic.wordCount }} words,
                            {{ topic.completedSentences }}/{{ topic.wordCount }} sentences
                          </div>
                        </v-expansion-panel-text>
                      </v-expansion-panel>
                    </v-expansion-panels>
                    <div v-if="courseTopics[course.id].length === 0" class="text-caption mt-2">
                      No topics yet. Upload materials to add topics.
                    </div>
                  </div>
                  <div v-else-if="loadingTopics[course.id]" class="text-caption mt-2">
                    Loading topics...
                  </div>
                  <v-btn
                    v-else
                    @click="loadCourseTopics(course.id)"
                    size="small"
                    variant="outlined"
                    class="mt-2"
                  >
                    Load Topics
                  </v-btn>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-card-text>
      </v-card>

      <!-- Edit Course Dialog -->
      <v-dialog v-model="showEditDialog" max-width="600px">
        <v-card>
          <v-card-title>Edit Course</v-card-title>
          <v-card-text>
            <v-form ref="editFormRef" @submit.prevent="updateCourse">
              <v-text-field
                v-model="editingCourse.name"
                label="Course Name"
                :rules="[rules.required, rules.maxLength(100)]"
                required
                class="mb-3"
              ></v-text-field>

              <v-textarea
                v-model="editingCourse.description"
                label="Description (Optional)"
                :rules="[rules.maxLength(500)]"
                rows="3"
                class="mb-3"
              ></v-textarea>

              <v-select
                v-model="editingCourse.type"
                :items="courseTypeOptions"
                label="Course Type"
                :rules="[rules.required]"
                required
                class="mb-3"
              ></v-select>

              <v-select
                v-model="editingCourse.courseLanguage"
                :items="courseLanguageOptions"
                label="Course Language"
                :rules="[rules.required]"
                required
                class="mb-3"
              ></v-select>

              <v-checkbox
                v-model="editingCourse.availableInFree"
                label="Available in Free Tier"
                class="mb-3"
              ></v-checkbox>

              <v-card-actions>
                <v-btn type="submit" variant="elevated" :loading="isUpdating">
                  Save Changes
                </v-btn>
                <v-btn @click="closeEditDialog" variant="outlined">Cancel</v-btn>
              </v-card-actions>
            </v-form>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-container>
  </v-window-item>
</template>

<script setup lang="ts">
import { ref, onMounted, reactive, watch, computed } from "vue";
import axios, { AxiosError } from "axios";
import { useCourseStore } from "@/store/courseStore";

// Type definitions
type CourseType = "LANGUAGE_LEVEL" | "SUBJECT" | "SCHOOL_COURSE" | "COMPANY_COURSE";
type CourseLanguage = "RU" | "EN";

interface Course {
  id: string;
  name: string;
  description?: string;
  type: CourseType;
  courseLanguage: CourseLanguage;
  availableInFree: boolean;
  wordCount?: number;
  topicCount?: number;
  userEnrollment?: {
    completedWords?: number;
    completedSentences?: number;
  };
}

interface Topic {
  id: string;
  name: string;
  description?: string;
  courseId: string;
  availableInFree: boolean;
  wordCount?: number;
  completedWords?: number;
  completedSentences?: number;
}

interface CourseForUpload {
  id: string;
  name: string;
}

interface CreateCoursePayload {
  name: string;
  description?: string;
  type: CourseType;
  courseLanguage: CourseLanguage;
  availableInFree: boolean;
}

// Enums from backend
const courseTypeOptions = [
  { title: "Language Level", value: "LANGUAGE_LEVEL" },
  { title: "Subject", value: "SUBJECT" },
  { title: "School Course", value: "SCHOOL_COURSE" },
  { title: "Company Course", value: "COMPANY_COURSE" },
];

const courseLanguageOptions = [
  { title: "Russian", value: "RU" },
  { title: "English", value: "EN" },
];

// Store
const courseStore = useCourseStore();

// Computed
const isSchoolUser = computed(() => courseStore.isSchoolUser);

// State
const showCreateForm = ref(false);
const isLoadingCourses = ref(false);
const isCreating = ref(false);
const isUploading = ref(false);
const isUpdating = ref(false);
const courses = ref<Course[]>([]);
const coursesForUpload = ref<CourseForUpload[]>([]);
const selectedCourseForUpload = ref<string | null>(null);
const uploadFile = ref<File | File[] | null>(null);
const uploadError = ref("");
const uploadSuccess = ref("");
const expandedCourses = ref<string[]>([]);
const courseTopics = ref<Record<string, Topic[]>>({});
const expandedTopics = ref<Record<string, string[]>>({});
const loadingTopics = ref<Record<string, boolean>>({});

// Forms
const createFormRef = ref<InstanceType<typeof import("vuetify/components").VForm> | null>(null);
const editFormRef = ref<InstanceType<typeof import("vuetify/components").VForm> | null>(null);

const newCourse = reactive<CreateCoursePayload>({
  name: "",
  description: "",
  type: "LANGUAGE_LEVEL",
  courseLanguage: "RU",
  availableInFree: false,
});

const editingCourse = reactive<CreateCoursePayload & { id: string }>({
  id: "",
  name: "",
  description: "",
  type: "LANGUAGE_LEVEL",
  courseLanguage: "RU",
  availableInFree: false,
});

const showEditDialog = ref(false);

// Validation rules
const rules = {
  required: (value: unknown): boolean | string => !!value || "This field is required",
  maxLength: (max: number) => (value: string): boolean | string =>
    !value || value.length <= max || `Maximum ${max} characters`,
  fileFormat: (files: File[] | null): boolean | string => {
    if (!files || files.length === 0) return true;
    const file = files[0];
    return (
      file.name.endsWith(".xlsx") ||
      "File must be an Excel file (.xlsx)"
    );
  },
  fileSize: (files: File[] | null): boolean | string => {
    if (!files || files.length === 0) return true;
    const file = files[0];
    const maxSize = 1000000; // 1MB in bytes
    return file.size <= maxSize || "File size must be less than 1MB";
  },
};

// Fetch all courses
async function fetchCourses() {
  isLoadingCourses.value = true;
  try {
    const response = await axios.get("/api/course/list");
    courses.value = response.data;
    coursesForUpload.value = response.data.map((course: Course) => ({
      id: course.id,
      name: `${course.name} (${course.type})`,
    }));
  } catch (error: unknown) {
    console.error("Failed to fetch courses", error);
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    alert("Failed to load courses: " + errorMessage);
  } finally {
    isLoadingCourses.value = false;
  }
}

// Create new course
async function createCourse() {
  if (!createFormRef.value) return;
  const { valid } = await createFormRef.value.validate();
  if (!valid) return;

  isCreating.value = true;
  try {
    const response = await axios.post("/api/course", newCourse);
    uploadSuccess.value = `Course "${response.data.name}" created successfully!`;
    uploadError.value = "";
    
    // Reset form
    Object.assign(newCourse, {
      name: "",
      description: "",
      type: "LANGUAGE_LEVEL",
      courseLanguage: "RU",
      availableInFree: false,
    });
    showCreateForm.value = false;
    
    // Refresh courses list
    await fetchCourses();
    
    // Auto-select newly created course for upload
    selectedCourseForUpload.value = response.data.id;
  } catch (error: unknown) {
    console.error("Failed to create course", error);
    if (error instanceof AxiosError && error.response?.data) {
      const data = error.response.data as { message?: string };
      alert("Failed to create course: " + (data.message || "Unknown error"));
    } else {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      alert("Failed to create course: " + errorMessage);
    }
  } finally {
    isCreating.value = false;
  }
}

function cancelCreate() {
  showCreateForm.value = false;
  Object.assign(newCourse, {
    name: "",
    description: "",
    type: "LANGUAGE_LEVEL",
    courseLanguage: "RU",
    availableInFree: false,
  });
}

// Upload materials
async function uploadMaterials() {
  if (!selectedCourseForUpload.value || !uploadFile.value) {
    uploadError.value = "Please select a course and file";
    return;
  }

  // Handle v-file-input: it can return File | File[] | null
  let file: File | null = null;
  if (uploadFile.value instanceof File) {
    file = uploadFile.value;
  } else if (Array.isArray(uploadFile.value) && uploadFile.value.length > 0) {
    file = uploadFile.value[0];
  }
  
  if (!file) {
    uploadError.value = "Please select a file";
    return;
  }

  // Validate file
  if (!file.name.endsWith(".xlsx")) {
    uploadError.value = "File must be an Excel file (.xlsx)";
    return;
  }
  
  if (file.size > 1000000) {
    uploadError.value = "File size must be less than 1MB";
    return;
  }

  isUploading.value = true;
  uploadError.value = "";
  uploadSuccess.value = "";

  try {
    const formData = new FormData();
    formData.append("file", file);

    await axios.post(
      `/api/files/course/${selectedCourseForUpload.value}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    uploadSuccess.value = "Materials uploaded successfully!";
    uploadFile.value = null;
    
    // Reload topics for the course if already expanded
    if (selectedCourseForUpload.value && expandedCourses.value.includes(selectedCourseForUpload.value)) {
      await loadCourseTopics(selectedCourseForUpload.value);
    }
  } catch (error: unknown) {
    console.error("Failed to upload materials", error);
    if (error instanceof AxiosError && error.response?.data) {
      const data = error.response.data as { message?: string };
      uploadError.value = "Upload failed: " + (data.message || "Unknown error");
    } else {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      uploadError.value = "Upload failed: " + errorMessage;
    }
  } finally {
    isUploading.value = false;
  }
}

// Load topics for a course
async function loadCourseTopics(courseId: string) {
  if (courseTopics.value[courseId]) {
    return; // Already loaded
  }

  loadingTopics.value[courseId] = true;
  try {
    const response = await axios.get(`/api/course/${courseId}/topics`);
    courseTopics.value[courseId] = response.data;
    expandedTopics.value[courseId] = [];
  } catch (error: unknown) {
    console.error("Failed to load topics", error);
    courseTopics.value[courseId] = [];
  } finally {
    loadingTopics.value[courseId] = false;
  }
}

// Edit course
function openEditDialog(course: Course) {
  Object.assign(editingCourse, {
    id: course.id,
    name: course.name,
    description: course.description || "",
    type: course.type,
    courseLanguage: course.courseLanguage,
    availableInFree: course.availableInFree,
  });
  showEditDialog.value = true;
}

function closeEditDialog() {
  showEditDialog.value = false;
  Object.assign(editingCourse, {
    id: "",
    name: "",
    description: "",
    type: "LANGUAGE_LEVEL",
    courseLanguage: "RU",
    availableInFree: false,
  });
}

async function updateCourse() {
  if (!editFormRef.value) return;
  const { valid } = await editFormRef.value.validate();
  if (!valid) return;

  isUpdating.value = true;
  try {
    const updateData = {
      name: editingCourse.name,
      description: editingCourse.description || undefined,
      type: editingCourse.type,
      courseLanguage: editingCourse.courseLanguage,
      availableInFree: editingCourse.availableInFree,
    };

    await axios.patch(`/api/course/${editingCourse.id}`, updateData);
    
    uploadSuccess.value = "Course updated successfully!";
    uploadError.value = "";
    closeEditDialog();
    
    // Refresh courses list
    await fetchCourses();
    
    // Reload topics if they were loaded
    if (courseTopics.value[editingCourse.id]) {
      delete courseTopics.value[editingCourse.id];
      if (expandedCourses.value.includes(editingCourse.id)) {
        await loadCourseTopics(editingCourse.id);
      }
    }
  } catch (error: unknown) {
    console.error("Failed to update course", error);
    if (error instanceof AxiosError && error.response?.data) {
      const data = error.response.data as { message?: string };
      alert("Failed to update course: " + (data.message || "Unknown error"));
    } else {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      alert("Failed to update course: " + errorMessage);
    }
  } finally {
    isUpdating.value = false;
  }
}

// Watch for course expansion to auto-load topics
watch(expandedCourses, async (newExpanded, oldExpanded) => {
  const newlyExpanded = newExpanded.filter((id) => !oldExpanded.includes(id));
  for (const courseId of newlyExpanded) {
    await loadCourseTopics(courseId);
  }
});

onMounted(() => {
  fetchCourses();
});
</script>

<style scoped>
.text-error {
  color: rgb(var(--v-theme-error));
}

.text-success {
  color: rgb(var(--v-theme-success));
}

/* School theme: black text on buttons (only on materjalid view) */
.school-courses-view :deep(.v-btn),
.school-courses-view :deep(.v-btn *) {
  color: #000000 !important;
}

/* School theme: black text in expansion panels (only on materjalid view) */
.school-courses-view :deep(.v-expansion-panel),
.school-courses-view :deep(.v-expansion-panel-title),
.school-courses-view :deep(.v-expansion-panel-title *),
.school-courses-view :deep(.v-expansion-panel-text),
.school-courses-view :deep(.v-expansion-panel-text *) {
  color: #000000 !important;
}

.school-courses-view :deep(.v-expansion-panel-title strong),
.school-courses-view :deep(.v-expansion-panel-text strong) {
  color: #000000 !important;
}

/* School theme: black text for loading/empty states (only on materjalid view) */
.school-courses-view .v-card-text > div {
  color: #000000 !important;
}
</style>